import { useState, useCallback } from 'react';
import { useConfig } from 'wagmi';
import { writeContract, waitForTransactionReceipt, estimateGas, getGasPrice } from 'wagmi/actions';
import { parseUnits, formatEther, decodeEventLog } from 'viem';
import { childPool } from '../../../utils/contract';

export const useChildpoolCreation = () => {
  const [isCreatingPool, setIsCreatingPool] = useState(false);
  const [isEstimatingGas, setIsEstimatingGas] = useState(false);
  const config = useConfig();

  // ✅ Include decimals parameter
  const estimateChildpoolCreationGas = useCallback(
    async (address, contractAddress, poolFeePercent, stakingAmount, metadata, tokenDecimals = 18) => {
      try {
        setIsEstimatingGas(true);

        // console.log(address, contractAddress, poolFeePercent, stakingAmount, metadata, tokenDecimals, 'estimateChildpoolCreationGas');

        const stakeAmountWei = parseUnits(stakingAmount?.toString(), tokenDecimals);
        const poolFeeBasisPoints = Math.round(parseFloat(poolFeePercent) * 100);

        const estimatedGas = await estimateGas(config, {
          address: contractAddress,
          abi: childPool.abi,
          functionName: 'createChildPool',
          args: [poolFeeBasisPoints, stakeAmountWei, metadata],
        });

        const gasPrice = await getGasPrice(config);
        const totalGasCost = estimatedGas * gasPrice;
        const totalGasCostETH = formatEther(totalGasCost);

        return {
          estimatedGas: estimatedGas?.toString(),
          gasPrice: gasPrice?.toString(),
          totalGasCostWei: totalGasCost?.toString(),
          totalGasCostETH: parseFloat(totalGasCostETH),
          totalGasCostETHFormatted: totalGasCostETH,
        };
      } catch (error) {
        console.error('Error estimating gas:', error);
        throw error;
      } finally {
        setIsEstimatingGas(false);
      }
    },
    [config]
  );

  // ✅ Include decimals parameter
  const createChildpool = useCallback(
    async (contractAddress, poolFeePercent, stakingAmount, metadataLink, tokenDecimals = 18) => {
      try {
        // console.log(contractAddress, poolFeePercent, stakingAmount, metadataLink, tokenDecimals, 'createChildpool');
        setIsCreatingPool(true);

        const stakeAmountWei = parseUnits(stakingAmount?.toString(), tokenDecimals);
        const poolFeePPM = Math.round(parseFloat(poolFeePercent) * 10_000);

        const hash = await writeContract(config, {
          address: contractAddress,
          abi: childPool.abi,
          functionName: 'createChildPool',
          args: [poolFeePPM, stakeAmountWei, metadataLink],
        });

        const receipt = await waitForTransactionReceipt(config, { hash });
        let poolAddress = null;
        let claimAddress = null;

        for (const log of receipt?.logs) {
          try {
            const decoded = decodeEventLog({
              abi: childPool.abi,
              data: log?.data,
              topics: log?.topics,
            });
            if (decoded?.eventName === "PoolCreated") {
              poolAddress = decoded?.args?.pool;
              claimAddress = decoded?.args?.claims;
              break;
            }
          } catch (err) {
            // console.log(err);
          }
        }

        return { receipt, hash, poolAddress, claimAddress };
      } catch (error) {
        console.error('Error creating pool:', error);
        throw error;
      } finally {
        setIsCreatingPool(false);
      }
    },
    [config]
  );

  return {
    isCreatingPool,
    isEstimatingGas,
    createChildpool,
    estimateChildpoolCreationGas,
  };
};
